import React from 'react';
import { FileText, Download, RefreshCw, AlertTriangle, Loader2, FilePlus2, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const VARIANT_META = {
  full: { label: 'Completo', badgeClass: 'bg-blue-100 text-blue-700' },
  lite: { label: 'Lite', badgeClass: 'bg-teal-100 text-teal-700' },
};

/**
 * Pannello "Documenti PDF" nel dettaglio preventivo store:
 * - Genera/Rigenera PDF Completo (6 pagine) e PDF Lite (3 pagine sintetiche)
 * - Versionati separatamente e salvati nel CRM (PREV-XXXX_FULL_v1, PREV-XXXX_LITE_v1, ...)
 * - Alert "PDF non aggiornato" per variante se il preventivo è cambiato dopo l'ultima generazione
 * - Storico versioni scaricabili
 */
export const QuotePdfPanel = ({ versions, variants, generatingVariant, onGenerate, onDownloadVersion }) => {
  const hasVersions = versions && versions.length > 0;
  const variantInfo = (v) => (variants && variants[v]) || {};

  const outdatedVariants = Object.keys(VARIANT_META).filter(
    (v) => variantInfo(v).latest_version && variantInfo(v).outdated
  );

  const generateButton = (variant, { icon: Icon, primary }) => {
    const info = variantInfo(variant);
    const exists = !!info.latest_version;
    const isGenerating = generatingVariant === variant;
    return (
      <Button
        className="w-full"
        variant={primary ? 'default' : 'outline'}
        onClick={() => onGenerate(variant)}
        disabled={!!generatingVariant}
        data-testid={variant === 'full' ? 'generate-pdf-btn' : 'generate-pdf-lite-btn'}
      >
        {isGenerating ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : exists ? (
          <RefreshCw className="h-4 w-4 mr-2" />
        ) : (
          <Icon className="h-4 w-4 mr-2" />
        )}
        {isGenerating
          ? 'Generazione...'
          : `${exists ? 'Rigenera' : 'Genera'} PDF ${VARIANT_META[variant].label}`}
      </Button>
    );
  };

  return (
    <Card data-testid="quote-pdf-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-600" />
          Documenti PDF
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {outdatedVariants.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700" data-testid="pdf-outdated-alert">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              {outdatedVariants.length === 2
                ? 'PDF Completo e Lite non aggiornati'
                : `PDF ${VARIANT_META[outdatedVariants[0]].label} non aggiornato`}{' '}
              rispetto ai dati correnti del preventivo. Rigenera per allinearli.
            </span>
          </div>
        )}

        {generateButton('full', { icon: FilePlus2, primary: true })}
        {generateButton('lite', { icon: Zap, primary: false })}
        <p className="text-[11px] text-muted-foreground -mt-1">
          Completo: proposta dettagliata (6 pagine) · Lite: sintesi rapida per il cliente (3 pagine)
        </p>

        {hasVersions && (
          <div className="space-y-2" data-testid="pdf-versions-list">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Versioni generate
            </p>
            {versions.map((v) => {
              const variant = v.variant || 'full';
              const meta = VARIANT_META[variant] || VARIANT_META.full;
              const info = variantInfo(variant);
              const isLatest = info.latest_version === v.version;
              return (
                <div
                  key={`${variant}-${v.version}`}
                  className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2"
                  data-testid={`pdf-version-${variant}-${v.version}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-xs ${meta.badgeClass}`}>{meta.label}</Badge>
                      <Badge variant="secondary" className="text-xs">v{v.version}</Badge>
                      {isLatest && !info.outdated && (
                        <Badge className="text-xs bg-green-100 text-green-700">Aggiornato</Badge>
                      )}
                      {isLatest && info.outdated && (
                        <Badge className="text-xs bg-amber-100 text-amber-700">Da rigenerare</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {new Date(v.generated_at).toLocaleString('it-IT')} · {v.generated_by_name}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => onDownloadVersion(v)}
                    title={`Scarica ${v.filename}`}
                    data-testid={`download-pdf-${variant}-v${v.version}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {!hasVersions && (
          <p className="text-xs text-muted-foreground text-center">
            Nessun PDF generato. Ogni PDF viene salvato nel CRM e versionato a ogni generazione.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
